/**
 * QUI JOUE. Le compte, le jeton, et les trois gestes qui les changent.
 *
 * Le compte est rechargé au montage si un jeton traîne dans le stockage : c'est ce qui fait qu'on
 * revient sur son aventure en rouvrant l'onglet, sans écran de connexion à traverser.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import { api, poseLeJeton, leJeton } from "./api/client.js";
import type { Compte } from "./api/types.js";

interface Session {
  compte: Compte | null;
  /** Vrai tant qu'on n'a pas encore su si le jeton en poche vaut quelque chose. */
  enChargement: boolean;
  connecte: (email: string, motDePasse: string) => Promise<void>;
  inscris: (email: string, motDePasse: string) => Promise<void>;
  deconnecte: () => Promise<void>;
  rafraichis: () => Promise<void>;
}

const Contexte = createContext<Session | null>(null);

export function FournisseurDeSession({ children }: { children: ReactNode }) {
  const [compte, poseLeCompte] = useState<Compte | null>(null);
  const [enChargement, poseLeChargement] = useState<boolean>(leJeton() !== null);

  const rafraichis = useCallback(async () => {
    if (!leJeton()) {
      poseLeCompte(null);
      poseLeChargement(false);
      return;
    }
    try {
      const { compte: recu } = await api.lis<{ compte: Compte }>("/auth/moi");
      poseLeCompte(recu);
    } catch {
      // Jeton mort : le client l'a déjà oublié sur le 401. On repart de la porte.
      poseLeCompte(null);
    } finally {
      poseLeChargement(false);
    }
  }, []);

  useEffect(() => {
    void rafraichis();
  }, [rafraichis]);

  const entre = useCallback(async (chemin: string, email: string, motDePasse: string) => {
    const { token, compte: recu } = await api.poste<{ token: string; compte: Compte }>(chemin, { email, motDePasse });
    poseLeJeton(token);
    poseLeCompte(recu);
  }, []);

  const valeur = useMemo<Session>(
    () => ({
      compte,
      enChargement,
      connecte: (email, motDePasse) => entre("/auth/connexion", email, motDePasse),
      inscris: (email, motDePasse) => entre("/auth/inscription", email, motDePasse),
      deconnecte: async () => {
        try {
          await api.poste("/auth/deconnexion");
        } finally {
          // Même si le serveur n'a pas répondu : le jeton part d'ici, sinon un joueur qui « se
          // déconnecte » sur un réseau coupé reste connecté sur la machine où il vient de partir.
          poseLeJeton(null);
          poseLeCompte(null);
        }
      },
      rafraichis
    }),
    [compte, enChargement, entre, rafraichis]
  );

  return <Contexte.Provider value={valeur}>{children}</Contexte.Provider>;
}

export function useSession(): Session {
  const session = useContext(Contexte);
  if (!session) throw new Error("useSession hors du fournisseur");
  return session;
}
